import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { ArrowsOut, ArrowCounterClockwise, Check, UploadSimple, Spinner, MagicWand, ArrowsClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";
import EdgeControls from "@/components/EdgeControls";
import { edgeRenderStyle, edgeAmountDefault, edgeVarianceDefault } from "@/lib/edgeStyles";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Frame an oversized image inside the square face — drag to move, pinch / scroll
// / slider to zoom. Stores only the framing (scale + object-position %), applied
// via CSS everywhere the face renders, so it's instant and reversible. You can
// also swap the photo and edit the AI description right here.
export default function SiloFaceFramer({ open, imageUrl, faceLabel, initialCrop, description, onDescriptionChange, onReplacePhoto, replacing, onDraftFromTier1, drafting, onRecreate, recreating, onRemoveBorder, hasBorder, edges = [], edge = "clean", edgeAmount, edgeVariance, onEdgeChange, onSave, onClose, onPrevFace, onNextFace, faceIndex, faceCount }) {
  const [scale, setScale] = useState(1);
  const [px, setPx] = useState(50);
  const [py, setPy] = useState(50);
  const [curEdge, setCurEdge] = useState(edge);
  const [amt, setAmt] = useState(typeof edgeAmount === "number" ? edgeAmount : edgeAmountDefault(edge));
  const [vr, setVr] = useState(typeof edgeVariance === "number" ? edgeVariance : edgeVarianceDefault(edge));
  const changeEdge = (val) => {
    const a = edgeAmountDefault(val);
    const v = edgeVarianceDefault(val);
    setCurEdge(val); setAmt(a); setVr(v);
    onEdgeChange && onEdgeChange(val, a, v);
  };
  const boxRef = useRef(null);
  const pointers = useRef(new Map());
  const dragStart = useRef(null);
  const pinchStart = useRef(null);

  useEffect(() => {
    if (!open) return;
    setScale(clamp(Number(initialCrop?.scale) || 1, 1, 4));
    setPx(initialCrop?.px ?? 50);
    setPy(initialCrop?.py ?? 50);
    setCurEdge(edge);
    setAmt(typeof edgeAmount === "number" ? edgeAmount : edgeAmountDefault(edge));
    setVr(typeof edgeVariance === "number" ? edgeVariance : edgeVarianceDefault(edge));
    // reset transient pointer state
    pointers.current.clear();
    dragStart.current = null;
    pinchStart.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imageUrl]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      dragStart.current = { x: e.clientX, y: e.clientY, px, py };
    } else if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      pinchStart.current = { dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1, scale };
      dragStart.current = null;
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    if (pointers.current.size >= 2 && pinchStart.current) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      setScale(clamp(pinchStart.current.scale * (dist / pinchStart.current.dist), 1, 4));
      return;
    }
    if (dragStart.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      // image follows the finger → object-position moves the opposite way
      setPx(clamp(dragStart.current.px - (dx / box.width) * 100, 0, 100));
      setPy(clamp(dragStart.current.py - (dy / box.height) * 100, 0, 100));
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 1) {
      const p = [...pointers.current.values()][0];
      dragStart.current = { x: p.x, y: p.y, px, py };
    } else if (pointers.current.size === 0) {
      dragStart.current = null;
    }
  };
  const onWheel = (e) => setScale((s) => clamp(s - e.deltaY * 0.002, 1, 4));

  const reset = () => { setScale(1); setPx(50); setPy(50); };
  const save = () => onSave({ scale: Math.round(scale * 100) / 100, px: Math.round(px), py: Math.round(py) });

  const style = { objectPosition: `${px}% ${py}%`, transform: `scale(${scale})`, transformOrigin: `${px}% ${py}%` };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md p-0 bg-[#FDFCF8] overflow-hidden flex flex-col max-h-[92dvh]" data-testid="silo-face-framer">
        <div className="px-5 pt-4 pb-2 shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Frame the image</div>
          <div className="font-serif-display text-lg leading-tight">{faceLabel}</div>
          {onPrevFace && (
            <div className="mt-2 flex items-center justify-between gap-2" data-testid="framer-face-nav">
              <button type="button" onClick={onPrevFace} data-testid="framer-prev-face"
                className="inline-flex items-center gap-1 text-xs text-[#1C1B1A] px-2.5 py-1 rounded-md border hairline bg-white hover:border-[#4A5D4E] transition-colors">
                <CaretLeft size={13} weight="bold" /> Prev
              </button>
              <span className="text-[11px] text-[#6B6A68] tabular-nums">Face {faceIndex} of {faceCount}</span>
              <button type="button" onClick={onNextFace} data-testid="framer-next-face"
                className="inline-flex items-center gap-1 text-xs text-[#1C1B1A] px-2.5 py-1 rounded-md border hairline bg-white hover:border-[#4A5D4E] transition-colors">
                Next <CaretRight size={13} weight="bold" />
              </button>
            </div>
          )}
          {/* Jun 2026 (Randy) — Edge / frame picker lives right here, and the
              tile above previews the chosen edge LIVE — exactly as it lands on
              the silo pill once saved. The edge is its own layer over the photo:
              "curved" & "torn paper" eat into the image; frames overlay it. */}
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            {edges?.length > 0 && (
              <select
                value={curEdge}
                onChange={(e) => changeEdge(e.target.value)}
                data-testid="framer-edge-select"
                title="Choose an edge / frame — previews live on the tile above"
                className="text-sm rounded-md border hairline bg-white px-2.5 py-1 text-[#1C1B1A] focus:border-[#4A5D4E] outline-none"
              >
                {edges.map((ed) => <option key={ed.id} value={ed.id}>{ed.label}</option>)}
              </select>
            )}
            {onRemoveBorder && (
              <button
                type="button"
                onClick={() => { onRemoveBorder(); setCurEdge("clean"); }}
                data-testid="framer-remove-border"
                className="inline-flex items-center text-xs text-[#B5503B] border border-[#B5503B]/40 rounded-md px-2.5 py-1 hover:bg-[#B5503B]/10 transition-colors"
              >
                Remove border
              </button>
            )}
          </div>
          {/* smart edge slider(s) — adjust the chosen edge live on the tile above */}
          <div className="mt-2">
            <EdgeControls edge={curEdge} amount={amt} variance={vr}
              onChange={({ amount, variance }) => { setAmt(amount); setVr(variance); }}
              onCommit={() => onEdgeChange && onEdgeChange(curEdge, amt, vr)} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-5">
            {/* The stage IS the silo tile — exact pill shape, so what you see here
                is exactly the slice of the photo that lands on the tile. */}
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1.5 text-center">This is your tile — drag the photo to choose what shows</div>
            <div
              ref={boxRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onWheel={onWheel}
              data-testid="framer-stage"
              className="relative w-full aspect-[540/184] rounded-xl overflow-hidden bg-[#1C1B1A] cursor-grab active:cursor-grabbing select-none ring-2 ring-[#1C1B1A]/15 shadow-[0_4px_18px_rgba(0,0,0,0.18)]"
              style={{ touchAction: "none" }}
            >
              {imageUrl && (
                <div className="cube-edge w-full h-full" data-edge={curEdge} style={edgeRenderStyle(curEdge, amt, vr).container}>
                  <img src={imageUrl} alt={faceLabel} draggable={false} className="cube-edge-img pointer-events-none" style={{ ...style, ...edgeRenderStyle(curEdge, amt, vr).img }} />
                  {edgeRenderStyle(curEdge, amt, vr).overlay && <div className="absolute inset-0 pointer-events-none" data-no-invert style={edgeRenderStyle(curEdge, amt, vr).overlay} />}
                </div>
              )}
              <div className="absolute inset-0 pointer-events-none opacity-25" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "33.33% 33.33%" }} />
            </div>
            <p className="text-xs text-[#6B6A68] mt-2 text-center">Drag to move · pinch / scroll / slider to zoom</p>
          </div>
          <div className="px-5 py-4 flex items-center gap-3">
            <ArrowsOut size={16} className="text-[#6B6A68] shrink-0" />
            <Slider value={[scale]} min={1} max={4} step={0.05} onValueChange={(v) => setScale(v[0])} data-testid="framer-zoom" className="flex-1" />
          </div>
          <div className="px-5 pb-3 space-y-2 border-t hairline pt-3">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={onReplacePhoto}
                disabled={replacing}
                data-testid="framer-replace-photo"
                className="inline-flex items-center text-sm text-[#1C1B1A] px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] transition-colors disabled:opacity-60"
              >
                {replacing ? <Spinner size={15} className="animate-spin mr-1.5" /> : <UploadSimple size={15} weight="bold" className="mr-1.5" />}
                {replacing ? "Replacing…" : "Replace photo"}
              </button>
              {onRecreate && (
                <button
                  type="button"
                  onClick={onRecreate}
                  disabled={recreating}
                  data-testid="framer-recreate"
                  className="inline-flex items-center text-sm text-[#1C1B1A] px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] transition-colors disabled:opacity-60"
                >
                  {recreating ? <Spinner size={15} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={15} weight="bold" className="mr-1.5" />}
                  {recreating ? "Recreating…" : "Recreate from AI"}
                </button>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1">Image description (what AI reads) — saves as you type</div>
              <Textarea
                value={description || ""}
                onChange={(e) => onDescriptionChange && onDescriptionChange(e.target.value)}
                placeholder="Describe what this image should picture…"
                data-testid="framer-description"
                className="text-sm min-h-[60px] resize-none"
              />
            </div>
            {onDraftFromTier1 && (
              <button
                type="button"
                onClick={onDraftFromTier1}
                disabled={drafting}
                data-testid="framer-draft-tier1"
                className="inline-flex items-center text-sm text-[#1C1B1A] px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] transition-colors disabled:opacity-60"
              >
                {drafting ? <Spinner size={15} className="animate-spin mr-1.5" /> : <MagicWand size={15} weight="duotone" className="mr-1.5" />}
                Draft from my Tier 1
              </button>
            )}
          </div>
        </div>

        <div className="px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] flex items-center justify-between gap-2 shrink-0 border-t hairline bg-[#FDFCF8]">
          <Button variant="ghost" onClick={reset} data-testid="framer-reset" className="text-[#6B6A68]"><ArrowCounterClockwise size={15} className="mr-1.5" /> Reset</Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="framer-cancel">Cancel</Button>
            <Button onClick={save} data-testid="framer-save" className="bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]"><Check size={15} className="mr-1.5" /> Save framing</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
